import React, { useState } from 'react';
import { Box, Collapse, Button, CircularProgress, Typography } from '@mui/material';
import { GroupWork } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SelectableAlertCard from '../shared/SelectableAlertCard';
import LazyAlertCard from '../shared/LazyAlertCard';
import AlertCardCollectionProvider from '../shared/AlertCardCollectionProvider';
import { theme, ThemedTypography } from '../../themes/technicalAlerts';

// Helper function to format group titles with translation
const formatGroupTitle = (group, t) => {
  const { key, groupBy } = group;
  
  switch (groupBy) {
    case 'temporal':
      const weekCount = parseInt(key) || 0;
      return t('weeksAgo', { count: weekCount }); // "Hace 5 semanas" / "5 weeks ago"
    case 'machine':
      return `${t('machine')} ${key}`; // "Machine M123"
    case 'product':
      return key; // Just show product name directly
    case 'location':
      return key; // Just show location name directly  
    case 'store_cluster':
      return key; // Just show raw cluster name (no prefix needed)
    default:
      return key;
  }
};

// Helper function to format revenue (moved from backend to frontend)
const formatRevenue = (amount) => {
  if (amount >= 1000) {
    return `€${(amount / 1000).toFixed(1)}k`;
  }
  return `€${Math.floor(amount)}`;  // Remove decimals for amounts under 1000
};

// Group header component - Minimal Notion/Apple style
const GroupHeader = ({ 
  group, 
  strategy, 
  isExpanded, 
  isLoading = false,
  onToggle,
  alertCount,
  totalRevenueLoss, // Raw revenue amount - frontend will format
  criticalCount,
  t
}) => {

  const handleToggleClick = () => {
    onToggle();
  };

  return (
    <Box
      onClick={handleToggleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '16px',
        py: '12px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        borderBottom: isExpanded ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)'
        }
      }}
    >
      {/* Left section - Group info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Expand/collapse chevron or loading spinner */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          width: '16px',
          height: '16px',
          justifyContent: 'center'
        }}>
          {isLoading ? (
            <CircularProgress size={12} sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
          ) : (
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
              <path 
                d="M1 1L5 5L1 9" 
                stroke="rgba(0, 0, 0, 0.3)" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>

        {/* Group title */}
        <ThemedTypography.H4>
          {formatGroupTitle(group, t)}
        </ThemedTypography.H4>
      </Box>

      {/* Right section - Metrics */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Alert count */}
        <ThemedTypography.Body2 sx={{ color: theme.colors.text.subtle }}>
          {t('alertCount', { count: alertCount })}
        </ThemedTypography.Body2>

        {/* Revenue loss */}
        {totalRevenueLoss > 0 && (
          <ThemedTypography.Body1 sx={{
            fontWeight: 600,
            color: theme.colors.alert
          }}>
            {formatRevenue(totalRevenueLoss)} {t('estimatedLost')}
          </ThemedTypography.Body1>
        )}

        {/* Critical indicator */}
        {criticalCount > 0 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: theme.colors.alert
          }} />
        )}
      </Box>
    </Box>
  );
};

// Optimized group content with pagination  
const GroupContent = ({ group, selectedRowKeys, onSelectionChange, expandedCards, onCardExpand, actionLoading, confirmBroken, confirmNotBroken, clientId, onDataRefresh, showToast }) => {
  const { t } = useTranslation();
  const ALERTS_PER_PAGE = 5; // Show only 5 alerts initially
  const [visibleCount, setVisibleCount] = useState(ALERTS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  

  // Memoize the status change callback to prevent unnecessary re-renders
  const handleStatusChange = React.useCallback((status, alertId) => {
    // Legacy compatibility handler - maintain single alert support
    if (status === 'not_broken') {
      return confirmNotBroken(alertId);
    } else if (status === 'mark_as_broken') {
      return confirmBroken(alertId);
    }
  }, [confirmNotBroken, confirmBroken]);
  
  // Reset pagination when group changes
  React.useEffect(() => {
    setVisibleCount(ALERTS_PER_PAGE);
  }, [group.key]);

  const visibleAlerts = group.alerts.slice(0, visibleCount);
  const hasMore = visibleCount < group.alerts.length;

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    // Simulate brief delay for UX (optional)
    await new Promise(resolve => setTimeout(resolve, 200));
    setVisibleCount(prev => Math.min(prev + ALERTS_PER_PAGE, group.alerts.length));
    setIsLoadingMore(false);
  };

  return (
    <Box sx={{
      px: '16px',
      pb: '16px',
    }}>
      {/* Cards Grid - Using new AlertCardCollectionProvider */}
      <AlertCardCollectionProvider
        onAlertAction={async (alertIds, action) => {
          // Use the updated API pattern with bulk support
          try {
            let result;
            if (action === 'not_broken') {
              result = await confirmNotBroken(alertIds);
            } else if (action === 'mark_as_broken') {
              result = await confirmBroken(alertIds);
            }
            
            return result !== false ? { success: true } : { success: false };
          } catch (error) {
            return { success: false, error: error.message };
          }
        }}
        onDataRefresh={onDataRefresh}
        onOperationSuccess={showToast}
        onOperationError={(error) => showToast(error, 'error')}
        clientId={clientId}
      >
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          py: 2
        }}>
          {visibleAlerts.map((alert) => (
            <LazyAlertCard
              key={alert.id}
              item={alert}
              alertKey={`${alert.id}`}
              clientId={clientId}
              showMachineId={true}
              showBadge={true}
              handleMachineIdClick={undefined} // TODO: Add navigation handler if needed
              onStatusChange={handleStatusChange}
              t={t} // Pass translation function
            />
          ))}
        </Box>
      </AlertCardCollectionProvider>

      {/* Load More Button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.12)',
              color: theme.colors.text.secondary,
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.24)',
                backgroundColor: 'rgba(0, 0, 0, 0.02)'
              }
            }}
          >
            {isLoadingMore ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading...
              </>
            ) : (
              `Show ${Math.min(ALERTS_PER_PAGE, group.alerts.length - visibleCount)} more alerts`
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Simplified smart grouping component - displays pre-grouped data from backend
const SmartGroupingSystem = ({
  groups = [],  // Now receives pre-grouped data instead of raw alerts
  metadata = null, // Metadata for ALL groups
  pagination = null, // Pagination info
  loading = false,
  selectedRowKeys = [],
  onSelectionChange,
  expandedCards = {},
  onCardExpand,
  expandedGroups = new Set(), // Now a Set from the hook
  onGroupToggle, // Handler from parent (hook function)
  groupingStrategy,
  actionLoading,
  confirmBroken,
  confirmNotBroken,
  clientId, // New props for AlertCardCollectionProvider
  onDataRefresh,
  showToast
}) => {
  const { t } = useTranslation();

  // Loading state
  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <ThemedTypography.H1 sx={{ mb: '24px' }}>
          {t('loadingGroups')}
        </ThemedTypography.H1>
      </Box>
    );
  }

  // Empty state
  if (!groups || groups.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 4,
        textAlign: 'center'
      }}>
        <GroupWork sx={{ fontSize: '48px', color: theme.colors.text.muted, mb: 2 }} />
        <ThemedTypography.H3 sx={{ mb: 1, color: theme.colors.text.secondary }}>
          {t('noAlertsToGroup')}
        </ThemedTypography.H3>
        <ThemedTypography.Body2 sx={{ color: theme.colors.text.muted }}>
          {t('smartGroupingWillOrganize')}
        </ThemedTypography.Body2>
      </Box>
    );
  }

  // Function to get the correct translation key based on grouping strategy
  const getGroupCountKey = (strategy) => {
    switch (strategy) {
      case 'store_cluster':
        return 'storeClusterCount';
      case 'machine':
        return 'machineCount';
      case 'product':
        return 'productCount';
      case 'temporal':
        return 'temporalCount';
      default:
        return 'groupCount'; // fallback
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Pre-grouped Data Display */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {groups.map((group) => {
          const isExpanded = expandedGroups.has(group.key);

          return (
            <Box 
              key={group.key}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: '12px',
                backgroundColor: theme.colors.background.white,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {/* Group Header */}
              <GroupHeader
                group={group}
                strategy={groupingStrategy}
                isExpanded={isExpanded}
                isLoading={false} // No loading since alerts are pre-loaded
                onToggle={() => onGroupToggle(group.key)}
                alertCount={group.alertCount}
                totalRevenueLoss={group.totalRevenueLoss}
                criticalCount={group.criticalCount}
                t={t}
              />

              {/* Group Content - Only render when actually expanded */}
              <Collapse in={isExpanded}>
                {isExpanded && (
                  <Box sx={{
                    animation: isExpanded ? 'groupExpandIn 0.3s ease-out' : 'none',
                    '@keyframes groupExpandIn': {
                      from: { 
                        opacity: 0, 
                        transform: 'translateY(-8px)' 
                      },
                      to: { 
                        opacity: 1, 
                        transform: 'translateY(0)' 
                      }
                    }
                  }}>
                    <GroupContent
                      group={group}
                      selectedRowKeys={selectedRowKeys}
                      onSelectionChange={onSelectionChange}
                      expandedCards={expandedCards}
                      onCardExpand={onCardExpand}
                      actionLoading={actionLoading}
                      confirmBroken={confirmBroken}
                      confirmNotBroken={confirmNotBroken}
                      clientId={clientId}
                      onDataRefresh={onDataRefresh}
                      showToast={showToast}
                    />
                  </Box>
                )}
              </Collapse>
            </Box>
          );
        })}
      </Box>

    </Box>
  );
};

export default SmartGroupingSystem;